import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Youtube, Facebook, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LOGO_URL = "/file.svg";

export const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer data-testid="footer" className="bg-emerald-950 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="Stigmaflora Agri Science" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-emerald-200 text-sm leading-relaxed">
              {t('footer.about_text')}
            </p>
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <span className="px-2 py-1 bg-emerald-900 rounded">ISO 9001:2015</span>
              <span>{t('footer.certified')}</span>
            </div>
            <div className="text-sm font-semibold text-emerald-300 pt-1">
              GST NO. : 36ABOCS1010Q1Z0
            </div>
            {/* Social Media Links */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://www.youtube.com/channel/UCZbaZT7Blkexm-qbvY3Hg7A"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="w-10 h-10 bg-emerald-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
              >
                <Youtube className="w-5 h-5 text-white" />
              </a>
              <a
                href="https://www.facebook.com/stigmaflora"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 bg-emerald-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a
                href="https://www.instagram.com/stigmaflora"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 bg-emerald-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-amber-500 font-semibold font-['Outfit'] mb-4">
              {t('footer.quick_links')}
            </h4>
            <ul className="space-y-3">
              {[
                { name: t('nav.home'), path: '/' },
                { name: t('nav.about'), path: '/about' },
                { name: t('nav.products'), path: '/products' },
                { name: t('nav.contact'), path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-emerald-200 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-amber-500 font-semibold font-['Outfit'] mb-4">
              {t('footer.categories')}
            </h4>
            <ul className="space-y-3">
              {[
                'Bio Stimulants',
                'Micro Nutrient Fertilizers',
                'Water Soluble Fertilizers',
                'IPM Tools',
              ].map((category) => (
                <li key={category}>
                  <Link
                    to={`/products?category=${encodeURIComponent(category)}`}
                    data-testid={`footer-category-${category.toLowerCase().replace(/ /g, '-')}`}
                    className="text-emerald-200 hover:text-white text-sm transition-colors"
                  >
                    {category}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-amber-500 font-semibold font-['Outfit'] mb-4">
              {t('footer.contact_us')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-emerald-200">{t('footer.customer_care')}</p>
                  <a
                    href="tel:7702099393"
                    data-testid="footer-phone"
                    className="text-white hover:text-amber-400 transition-colors"
                  >
                    7702099393
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:customercare@stigmaflora.com"
                  data-testid="footer-email"
                  className="text-white hover:text-amber-400 text-sm transition-colors break-all"
                >
                  customercare@stigmaflora.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-emerald-200">
                  {t('footer.address_l1')}<br />
                  {t('footer.address_l2')}<br />
                  {t('footer.address_l3')}
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-emerald-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-emerald-400">
              {t('footer.rights', { year: new Date().getFullYear() })}
            </p>
            <a
              href="https://www.stigmaflora.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-website"
              className="text-sm text-emerald-200 hover:text-amber-400 transition-colors"
            >
              www.stigmaflora.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
